<template>
  <div class="locations" :class="cssClass">
    <h3
        class="margin-0"
        :class="{
        'text-danger': store.error && store.requireFields?.includes('locations'),
      }"
        >
      <span class="locations-title">
        {{ t('measurement-location').toUpperCase() }}
      </span>

      <div
          v-if="cssClass !== 'vms' && sortedLocations.length"
          class="select-all-container"
      >
        <span class="select-all-text">
          {{ allSelected ? t('unmark') : t('mark') }} {{ t('all').toLowerCase() }}
        </span>

            <input-checkbox
                class="select-all-checkbox"
                field-name="selectAll"
                field-value=""
                :checkedCondition="allSelected"
                @clicked="setAllFilter()"
            />
      </div>
    </h3>

    <table class="table">
      <thead>
        <tr>
          <th class="sign" scope="col">></th>
          <th scope="col">Id</th>
          <th scope="col">{{ t('location-name') }}</th>
          <th scope="col" class="path-number">{{ t('road-no') }}</th>
          <th scope="col">{{ t('chainage') }}</th>
          <th scope="col" class="select-col">{{ t('select') }}</th>
        </tr>
      </thead>

      <tbody v-if="sortedLocations.length">
        <tr v-for="loc in sortedLocations" :key="loc.id">
          <td scope="row">
            <button
              type="button"
              class="status-link"
              @click.stop="goToLatestStatus(loc.id)"
              :title="t('location-statuses-history')"
            >
              <img :src="isActive(loc) ? availableIcon : unavailableIcon" alt="Status" />
            </button>
          </td>
          <td>{{ loc.id }}</td>
          <td>{{ loc.shortName }}</td>
          <td>{{ loc.roadNumber }}</td>
          <td>{{ loc.chainage }}</td>
          <td class="check">
            <input-checkbox
              :field-name="String(loc.id)"
              field-value=""
              :checkedCondition="isChecked(loc.id)"
              @clicked="setFilter(loc.id)"
            />
          </td>
        </tr>
      </tbody>

      <tbody v-else>
        <tr>
          <td colspan="6">
            <p class="no-data center-aligned">{{ t('no-data') }}</p>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import InputCheckbox from "../InputCheckbox.vue";
import availableIcon from "@/assets/icons/dostepny.png";
import unavailableIcon from "@/assets/icons/niedostepny.png";
import { isSG } from "@/config/appVersion";
import FiltersService from "@/services/FiltersService";

export default {
  name: "LocationList",
  components: { InputCheckbox },
  props: {
    store: { required: true },
    cssClass: String,

    // ✅ parametr "po jakim czasie offline" (minuty)
    // 8h = 480 minut
    freshnessThresholdMinutes: { type: Number, default: isSG ? 480 : 30 },

    // jak często odświeżać statusy z backendu (ms)
    // 5 minut: nie podtrzymuje sztucznie aktywności sesji przy krótkich timeoutach bezczynności
    statusPollIntervalMs: { type: Number, default: 5 * 60 * 1000 },

    // jeśli backend zwraca unix seconds, ustaw na true
    lastTimeIsUnixSeconds: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false,
      error: null,

      // reaktywne "tyknięcie" czasu dla przerysowania statusów
      tick: 0,
      timer: null,
      hasBeenActivated: false,
      refreshInFlight: null,

      availableIcon,
      unavailableIcon,
    };
  },

  async created() {
    // ✅ od razu przy wejściu pobierz świeże lokalizacje + przerysuj statusy
    await this.refreshNow({ force: true });
  },

  mounted() {
    this.startAutoRefresh();
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    window.addEventListener("focus", this.onWindowFocus);
  },

  async activated() {
    this.startAutoRefresh();

    // Przy keep-alive: odświeżaj dane za każdym kolejnym wejściem na komponent.
    if (this.hasBeenActivated) {
      await this.refreshNow({ force: true });
    }
    this.hasBeenActivated = true;
  },

  deactivated() {
    this.stopAutoRefresh();
  },

  beforeUnmount() {
    this.stopAutoRefresh();
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    window.removeEventListener("focus", this.onWindowFocus);
  },

  watch: {
    "store.locations"(newVal) {
      this.loading = !newVal || newVal.length === 0;
    },
  },

  computed: {
    sortedLocations() {
      const arr = Array.isArray(this.store.locations) ? this.store.locations : [];
      return [...arr].sort((a, b) => Number(a.id) - Number(b.id));
    },
    selectedIdSet() {
      const ids = this.store?.filters?.locations || [];
      return new Set(ids.map((x) => Number(x)));
    },
    allSelected() {
      const locs = this.sortedLocations;
      if (!locs.length) return false;
      return locs.every((l) => this.selectedIdSet.has(Number(l.id)));
    },
    thresholdMs() {
      return this.freshnessThresholdMinutes * 60 * 1000;
    },
  },

  methods: {
    onVisibilityChange() {
      if (!document.hidden) {
        this.refreshNow({ force: true });
      }
    },

    onWindowFocus() {
      this.refreshNow({ force: true });
    },

    startAutoRefresh() {
      this.stopAutoRefresh();
      this.timer = setInterval(async () => {
        await this.refreshNow({ force: true });
      }, this.statusPollIntervalMs);
    },

    stopAutoRefresh() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },

    async fetchFreshLocations() {
      const data = await FiltersService.getLocations();
      if (typeof this.store?.$patch === "function") {
        const patch = { locations: Array.isArray(data) ? data : [] };

        if (Array.isArray(this.store.laneNumbers)) {
          const lanesSet = new Set();
          patch.locations.forEach((location) => {
            const laneCount = Array.isArray(location?.lanes) ? location.lanes.length : 0;
            for (let i = 1; i <= laneCount; i++) {
              lanesSet.add(i);
            }
          });
          patch.laneNumbers = [...lanesSet].sort((a, b) => a - b);
        }

        this.store.$patch(patch);
        return;
      }

      await this.store.getLocations?.();
    },

    async refreshNow({ force = false } = {}) {
      if (this.refreshInFlight) {
        await this.refreshInFlight;
        return;
      }

      this.refreshInFlight = (async () => {
        if (force) {
          try {
            await this.fetchFreshLocations();
          } catch (e) {
            await this.store.getLocations?.();
          }
        } else {
          await this.store.getLocations?.();
        }
        this.tick++;
      })();

      try {
        await this.refreshInFlight;
      } finally {
        this.refreshInFlight = null;
      }
    },

    setFilter(id) {
      this.store.addItem?.("LocationList", Number(id));
    },
    isChecked(id) {
      return this.selectedIdSet.has(Number(id));
    },
    setAllFilter() {
      this.store.checkAllLocations?.();
    },
    goToLatestStatus(locationId) {
      this.$router.push({ name: "LocationStatusLatestView", params: { id: Number(locationId) } });
    },

    parseTimestamp(value) {
      if (value == null) return NaN;

      if (typeof value === "number") {
        return this.lastTimeIsUnixSeconds ? value * 1000 : value;
      }

      const trimmed = String(value).trim();

      // liczba w stringu
      if (/^\d+$/.test(trimmed)) {
        const n = Number(trimmed);
        return this.lastTimeIsUnixSeconds ? n * 1000 : n;
      }

      // ISO / date string
      const ms = Date.parse(trimmed);
      return Number.isNaN(ms) ? NaN : ms;
    },

    isActive(loc) {
      // ✅ zależność od tick = wymusza reaktywność
      void this.tick;

      const ts = this.parseTimestamp(loc?.lastVehicleTime);
      if (Number.isNaN(ts)) return false;

      return Date.now() - ts <= this.thresholdMs;
    },
  },
};
</script>

<style scoped>
/* NAGŁÓWEK */
.locations h3 {
  padding: 0.85rem 1rem 0.9rem 1rem;
  width: 100%;
  border-right: 1px solid #bfbfbf;
  border-bottom: 1px solid #bfbfbf;
  border-top: 1px solid #bfbfbf;

  display: flex;          /* tytuł + "zaznacz wszystkie" + checkbox w jednej linii */
  align-items: center;    /* pionowe wyśrodkowanie */
}

.locations-title {
  margin-right: 1rem;
}

/* tekst "zaznacz wszystkie" + checkbox po PRAWEJ stronie nagłówka */
.select-all-container {
  margin-left: auto;      /* wypchnij cały blok na prawo */
  display: flex;
  align-items: center;
  gap: 0;
  padding-right: 9px;    /* utrzymuje oś checkboxa względem kolumny "select" */
}

.select-all-text {
  font-size: 12px;
  margin-right: 0.5rem;
}

/* większy checkbox tylko dla "zaznacz wszystkie" w headerze */
.select-all-container :deep(.select-all-checkbox.container),
.select-all-container :deep(.select-all-checkbox.container2) {
  width: 22px;
  height: 22px;
  min-width: 22px;
  min-height: 22px;
  margin: 0;
  transform: none;
}

/* TABELA */
.locations table {
  width: 100%;
  border-collapse: collapse;
}

.locations th,
.locations td {
  text-align: center;
  vertical-align: middle;
  font-weight: normal;
  font-size: 12px;
  padding: 0.75rem;
}

/* obramowania wierszy */
.locations tr {
  border-right: 1px solid #bfbfbf;
  border-bottom: 1px solid #bfbfbf;
}

.locations td.check span {
  margin: 0 auto;
  margin-top: 0.1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.75rem;
}

.locations th.select-col,
.locations td.check {
  width: 74px;
}

/* kolumna z ">" */
.sign {
  color: #eaeaea;
  font-weight: 1000;
  font-size: 15px;
}

/* numer drogi, żeby się nie łamał */
.path-number {
  white-space: nowrap;
}

.status-link {
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
}
</style>
